/**
 * Metodologías visibles para el tenant (las del producto y las propias).
 *
 * Una sola query compartida por el catálogo, el detalle de una metodología,
 * el selector "genérica / de la metodología" y el asistente de proyecto.
 * `i18n.language` va en la clave: el backend resuelve `name`/`description`
 * con `Accept-Language`.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { listMethodologies, type Methodology } from '../api/methodologies'
import { retryUnlessForbiddenTier } from '../api/queryRetry'
import { useAccessToken } from '../auth/useAccessToken'

/** Prefijo para invalidar todo lo de metodologías tras una mutación. */
export const METHODOLOGIES_QUERY_KEY = ['methodologies'] as const

export function useMethodologies() {
  const { i18n } = useTranslation()
  const { accessToken } = useAccessToken()
  const query = useQuery({
    queryKey: [...METHODOLOGIES_QUERY_KEY, 'list', i18n.language],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listMethodologies(accessToken)
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })
  const byId = useMemo(
    () =>
      new Map<string, Methodology>(
        (query.data ?? []).map((m) => [m.id, m] as const),
      ),
    [query.data],
  )
  return { ...query, methodologies: query.data ?? [], byId }
}
